import { memo, useMemo } from 'react';
import { Line } from '@react-three/drei';
import {
  HALFSPACES_DIVIDER_XS,
  HALFSPACES_STROKE_HEX,
} from '../tactics2d/HalfSpacesLayer2D';

/**
 * 3D version of the Sky-MNF half-spaces overlay. Four dashed yellow
 * lines lying flat on the pitch (Y ≈ 0.011, above the analytical
 * overlays so the dividers stay legible on top of Spacing /
 * pitchControl / heatmap fills).
 *
 * Reuses the divider positions from the 2D layer so 2D + 3D agree
 * exactly on channel widths.
 */

const FIELD_LENGTH_M = 105;
const HALF_L = FIELD_LENGTH_M / 2;

export interface HalfSpacesLayer3DProps {
  opacity: number;
}

function HalfSpacesLayer3DInner({ opacity }: HalfSpacesLayer3DProps) {
  // Pre-build each divider's two endpoints. Memoised so the dividers
  // aren't re-allocated on every parent render.
  const dividers = useMemo(() => {
    return HALFSPACES_DIVIDER_XS.map((worldX, idx) => ({
      key: idx,
      points: [
        [worldX, 0.011, -HALF_L] as [number, number, number],
        [worldX, 0.011, HALF_L] as [number, number, number],
      ],
    }));
  }, []);

  return (
    <group raycast={() => undefined} renderOrder={3}>
      {dividers.map(({ key, points }) => (
        <Line
          key={key}
          points={points}
          color={HALFSPACES_STROKE_HEX}
          worldUnits
          lineWidth={0.18}
          dashed
          dashSize={1.4}
          gapSize={1.0}
          transparent
          opacity={opacity}
          depthWrite={false}
        />
      ))}
    </group>
  );
}

export const HalfSpacesLayer3D = memo(HalfSpacesLayer3DInner);
